import { BufferAttribute, BufferGeometry } from 'three';

/**
 * Extends the jaw sheet's outer rim(s) to one plane with a clean vertical
 * skirt, the way an extrude does it: a COPY of each rim loop is laid flat on
 * the common target plane and a band of quads bridges the two.
 *
 * This replaces flattening for the fused export. Flattening MOVED the scan's
 * border vertices onto the plane, so the "wall" was whatever the last row of
 * scan triangles stretched into — slanted, uneven, different height at every
 * point, and it needed half-edge surgery (collapse, band decimation) that the
 * DS could refuse. The skirt is pure geometry: every wall face is vertical
 * and the new bottom ring is planar by construction — and the direct offset
 * then bridges that ring into the flat base the print stands on.
 *
 * Before anything is hung, `trimRings` rows of faces are STRIPPED off each
 * selected rim. The scan's boundary row is junk — curled, folded-under
 * slivers the scanner leaves at the edge of coverage — and a vertical
 * curtain hung off it cuts straight through them, which a viewport never
 * shows and a slicer prints as a band of holes along the whole junction.
 * Two rows back the surface is real geometry.
 *
 * Meant to run on the FUSED, REPAIRED sheet: there the tooth sockets are
 * interior topology and the walks cannot derail into defect slits, so the
 * only long border loops left are the real rims — the outer perimeter, and
 * the lingual edge when the review trim left the gum an open band. Both are
 * skirted to the same plane; which ones qualify is an absolute perimeter
 * threshold (60 mm) — a real rim measures well over 100 mm around, while the
 * largest interproximal window opened by tooth movement measured ~35 mm, and
 * a skirt hung off a window would drop a curtain through the middle of the
 * model. The windows are the manifold repair's to fill, not ours.
 *
 * The bottom rings are Laplacian-smoothed in the plane so the skirt does not
 * inherit the rim's pleats; the wall fans slightly to follow, which is the
 * point — geometric detail is worthless down here.
 *
 * Winding: for a border edge the sheet walks a→b, the skirt attaches
 * (b, a, a′) + (b, a′, b′), so the shared edges pair opposite and the skirt
 * continues the sheet's orientation; the only new borders are the bottom
 * rings.
 */
export function extrudeGumSkirt(
  geometry: BufferGeometry,
  opens: 'down' | 'up',
  extendMm = 1,
  smoothPasses = 12,
  minPerimeterMm = 60,
  trimRings = 2,
  /**
   * Only faces this small get stripped as boundary junk. The scanner's edge
   * slivers are a fraction of this; anything bigger touching the rim is real
   * geometry already — and stripping big faces once moved a coarse rim
   * millimetres inward, which hung the curtain INSIDE the gum and read back
   * as hundreds of inner-wall escapes through it.
   */
  junkEdgeMm = 0.5,
): { planeY: number; skirted: number; loops: number; trimmedFaces: number } {
  if (!geometry.getIndex()) throw new Error('extrudeGumSkirt: geometry must be indexed');

  // Strip the scanner-junk boundary rows off the rims first. Each pass drops
  // the small faces touching a selected rim's vertices; the border re-derives
  // one row further into real geometry.
  let trimmedFaces = 0;
  for (let ring = 0; ring < trimRings; ring++) {
    const rims = selectRims(geometry, minPerimeterMm);
    if (rims.length === 0) break;
    const onRim = new Set<number>();
    for (const rim of rims) for (const v of rim.walk) onRim.add(v);
    const index = geometry.getIndex()!;
    const position = geometry.getAttribute('position') as BufferAttribute;
    const tris = index.array as Uint32Array;
    const array = position.array as Float32Array;
    const kept: number[] = [];
    let dropped = 0;
    for (let f = 0; f < tris.length; f += 3) {
      const touches = onRim.has(tris[f]) || onRim.has(tris[f + 1]) || onRim.has(tris[f + 2]);
      let longest = 0;
      if (touches) {
        for (let e = 0; e < 3; e++) {
          const a = tris[f + e] * 3;
          const b = tris[f + ((e + 1) % 3)] * 3;
          longest = Math.max(
            longest,
            Math.hypot(array[a] - array[b], array[a + 1] - array[b + 1], array[a + 2] - array[b + 2]),
          );
        }
      }
      if (touches && longest <= junkEdgeMm) {
        dropped++;
        continue;
      }
      kept.push(tris[f], tris[f + 1], tris[f + 2]);
    }
    if (dropped === 0) break;
    trimmedFaces += dropped;
    geometry.setIndex(kept);
  }

  const position = geometry.getAttribute('position') as BufferAttribute;
  const vertexCount = position.count;
  const rims = selectRims(geometry, minPerimeterMm);
  const loops = walkBorderLoops(geometry).length;
  if (rims.length === 0) return { planeY: Number.NaN, skirted: 0, loops, trimmedFaces };

  // One common plane for every rim, so the whole print stands flat.
  let targetY = opens === 'down' ? Infinity : -Infinity;
  for (const rim of rims) {
    for (const v of rim.walk) {
      const y = position.getY(v);
      targetY = opens === 'down' ? Math.min(targetY, y) : Math.max(targetY, y);
    }
  }
  targetY += opens === 'down' ? -extendMm : extendMm;

  const grownVertices = rims.reduce((sum, rim) => sum + rim.walk.length, 0);
  const grownPositions = new Float32Array((vertexCount + grownVertices) * 3);
  grownPositions.set(position.array as Float32Array);
  const oldIndex = geometry.getIndex()!.array as Uint32Array;
  const grownIndex = new Uint32Array(oldIndex.length + grownVertices * 6);
  grownIndex.set(oldIndex);

  let vertexCursor = vertexCount;
  let indexCursor = oldIndex.length;
  for (const rim of rims) {
    const loop = rim.walk;
    // Bottom ring: the loop projected onto the plane, then smoothed IN the
    // plane so the skirt sheds the rim's pleats.
    const ring = loop.map((v) => [position.getX(v), position.getZ(v)]);
    const next = ring.map((p) => [...p]);
    for (let pass = 0; pass < smoothPasses; pass++) {
      for (let i = 0; i < ring.length; i++) {
        const prev = ring[(i - 1 + ring.length) % ring.length];
        const after = ring[(i + 1) % ring.length];
        next[i][0] = ring[i][0] + 0.5 * ((prev[0] + after[0]) / 2 - ring[i][0]);
        next[i][1] = ring[i][1] + 0.5 * ((prev[1] + after[1]) / 2 - ring[i][1]);
      }
      for (let i = 0; i < ring.length; i++) {
        ring[i][0] = next[i][0];
        ring[i][1] = next[i][1];
      }
    }

    const bottomBase = vertexCursor;
    for (let i = 0; i < loop.length; i++) {
      const at = vertexCursor * 3;
      grownPositions[at] = ring[i][0];
      grownPositions[at + 1] = targetY;
      grownPositions[at + 2] = ring[i][1];
      vertexCursor++;
    }
    for (let i = 0; i < loop.length; i++) {
      const a = loop[i];
      const b = loop[(i + 1) % loop.length];
      const aBottom = bottomBase + i;
      const bBottom = bottomBase + ((i + 1) % loop.length);
      grownIndex[indexCursor++] = b;
      grownIndex[indexCursor++] = a;
      grownIndex[indexCursor++] = aBottom;
      grownIndex[indexCursor++] = b;
      grownIndex[indexCursor++] = aBottom;
      grownIndex[indexCursor++] = bBottom;
    }
  }

  geometry.setAttribute('position', new BufferAttribute(grownPositions, 3));
  geometry.setIndex(new BufferAttribute(grownIndex, 1));
  return { planeY: targetY, skirted: rims.length, loops, trimmedFaces };
}

export type BorderLoop = { walk: number[]; perimeter: number };

/**
 * Closed border loops, walked edge by edge. Edge-keyed on purpose: where two
 * loops touch, a vertex has two open edges leaving it and a vertex-keyed map
 * silently drops one — the same trap borderEdgesOf documents. Walks that
 * never close (a chain broken by a junction) are discarded.
 */
function walkBorderLoops(geometry: BufferGeometry): BorderLoop[] {
  const index = geometry.getIndex()!;
  const position = geometry.getAttribute('position') as BufferAttribute;
  const vertexCount = position.count;

  const directed = new Set<number>();
  for (let i = 0; i < index.count; i += 3) {
    for (let e = 0; e < 3; e++) {
      directed.add(index.getX(i + e) * vertexCount + index.getX(i + ((e + 1) % 3)));
    }
  }
  const openOut = new Map<number, number[]>();
  for (let i = 0; i < index.count; i += 3) {
    for (let e = 0; e < 3; e++) {
      const a = index.getX(i + e);
      const b = index.getX(i + ((e + 1) % 3));
      if (directed.has(b * vertexCount + a)) continue;
      if (!openOut.has(a)) openOut.set(a, []);
      openOut.get(a)!.push(b);
    }
  }

  const usedEdge = new Set<number>();
  const loops: BorderLoop[] = [];
  const array = position.array as Float32Array;
  openOut.forEach((tos, from) => {
    for (const to of tos) {
      if (usedEdge.has(from * vertexCount + to)) continue;
      const walk = [from];
      let at = from;
      let next = to;
      let closed = false;
      for (let guard = 0; guard < 100000; guard++) {
        usedEdge.add(at * vertexCount + next);
        if (next === from) {
          closed = true;
          break;
        }
        walk.push(next);
        const outs = (openOut.get(next) ?? []).filter((o) => !usedEdge.has(next * vertexCount + o));
        if (outs.length === 0) break;
        at = next;
        next = outs[0];
      }
      if (!closed || walk.length < 3) continue;
      let perimeter = 0;
      for (let i = 0; i < walk.length; i++) {
        const a = walk[i] * 3;
        const b = walk[(i + 1) % walk.length] * 3;
        perimeter += Math.hypot(
          array[a] - array[b],
          array[a + 1] - array[b + 1],
          array[a + 2] - array[b + 2],
        );
      }
      loops.push({ walk, perimeter });
    }
  });
  return loops;
}

/**
 * The loops that are real rims, by an ABSOLUTE perimeter threshold. It was
 * relative to the longest loop once, and the repair judged by its own longest
 * — after the trim fragmented a coarse rim into several long loops, the two
 * rules disagreed about the middle sizes: the skirt took one, the repair
 * protected the rest, and the shell came out with five rim loops and open
 * edges. One absolute number keeps every stage of the pipeline in agreement,
 * and the sizes are far apart anyway: the largest interproximal window
 * measures ~25 mm around, the shortest genuine rim well over 100 mm.
 */
export function selectRims(geometry: BufferGeometry, minPerimeterMm: number): BorderLoop[] {
  return walkBorderLoops(geometry).filter((l) => l.perimeter >= minPerimeterMm);
}
